import React, { useState } from 'react';
import { Mail, MessageSquare, MapPin, Send, CheckCircle2, AlertCircle, Loader2, X } from 'lucide-react';
import SEO from '../components/SEO';
import './Contact.css';

const Contact = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: 'success', // 'success' | 'error'
    title: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch("/.netlify/functions/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(form),
      });

      const contentType = res.headers.get("content-type") || "";
      let data = {};
      if (contentType.includes("application/json")) {
        data = await res.json();
      }

      if (res.ok) {
        setModalState({
          isOpen: true,
          type: 'success',
          title: 'Message Sent Successfully',
          message: `Thank you ${form.name}! We have received your message and sent a confirmation email to ${form.email}. Our team will contact you within 24 hours.`
        });
        setForm({ name: "", email: "", service: "", message: "" });
      } else {
        setModalState({
          isOpen: true,
          type: 'error',
          title: 'Submission Failed',
          message: data.message || 'We could not send your message at this moment. Please check your details and try again.'
        });
      }

    } catch (error) {
      console.error(error);
      setModalState({
        isOpen: true,
        type: 'error',
        title: 'Connection Error',
        message: 'An unexpected error occurred. Please check your internet connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setModalState({ ...modalState, isOpen: false });
  };

  return (
    <div className="contact-container">
      <SEO 
        title="Contact Us | Get a Free Technical Audit | Vektrixo Digital" 
        description="Get in touch with Vektrixo Digital for web development, software QA testing, and business strategy. Request a free performance and SEO scan for your website."
        keywords="Contact Vektrixo Digital, Software Testing Inquiry, Web Development Quote, Free Website Technical Audit Pune"
        canonical="https://www.vektrixo.com/contact"
      />

      {/* Header */}
      <section className="contact-header">
        <h1>Let's Create Something <span className="highlight">Great</span></h1>
        <p>Ready to scale? Drop us a message and we’ll get back within 24 hours.</p>
      </section>

      <section className="contact-content">

        {/* Info */}
        <div className="contact-info">

          <div className="info-item">
            <div className="info-icon"><Mail size={22} /></div>
            <div>
              <h4>Email Us</h4>
              <p>info@vektrixo.com</p>
            </div>
          </div>

          <div className="info-item">
            <div className="info-icon"><MessageSquare size={22} /></div>
            <div>
              <h4>Support</h4>
              <p>10 AM - 7 PM IST</p>
            </div>
          </div>

          <div className="info-item">
            <div className="info-icon"><MapPin size={22} /></div>
            <div>
              <h4>Location</h4>
              <p>Pune, Maharashtra, India</p>
            </div>
          </div>

        </div>

        {/* Form */}
        <div className="contact-form-wrapper">

          <form onSubmit={handleSubmit} className="professional-form">

            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>

            <div className="form-group">
              <label>Service</label>
              <select
                required
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
              >
                <option value="">Select Service</option>
                <option value="web-dev">Web Development</option>
                <option value="design">Design</option>
                <option value="qa">Software Testing</option>
                <option value="marketing">Marketing</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label>Message</label>
              <textarea
                rows="5"
                required
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Describe your project or technical query..."
              />
            </div>

            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="spinner-icon" /> Sending Message...
                </>
              ) : (
                <>
                  Send Message <Send size={18} />
                </>
              )}
            </button>

          </form>

        </div>

      </section>

      {/* CONFIRMATION POPUP MODAL */}
      {modalState.isOpen && (
        <div className="custom-modal-overlay" onClick={closeModal}>
          <div 
            className={`custom-modal-card ${modalState.type}`} 
            onClick={(e) => e.stopPropagation()}
          >
            <button className="modal-close-x" onClick={closeModal} title="Close">
              <X size={18} />
            </button>

            <div className="modal-icon-wrapper">
              {modalState.type === 'success' ? (
                <CheckCircle2 size={32} color="#38bdf8" />
              ) : (
                <AlertCircle size={32} color="#ef4444" />
              )}
            </div>

            <h3>{modalState.title}</h3>
            <p>{modalState.message}</p>

            <button className="modal-action-btn" onClick={closeModal}>
              {modalState.type === 'success' ? 'Got it' : 'Close'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;